import { useAuth, useSignIn } from '@clerk/clerk-react';
import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useIntercom } from 'react-use-intercom';

import BlockingLoader from '~components/commons/blocking-loader/blocking-loader';

import { useQueryParams } from '~hooks/locations/useQueryParams';

import actionTypes from '~actionTypes';
import { sessionNamespacer } from '~reducers/session';
import { SESSION_ENV_KEY } from '~utils/config';
import { handleException } from '~utils/handleException';

export const ImpersonationHandler: React.FC = () => {
	const dispatch = useDispatch();

	const { isSignedIn, signOut } = useAuth();
	const { shutdown: shutdownIntercomChat } = useIntercom();

	const { isLoaded, signIn, setActive } = useSignIn();
	const { queryParams } = useQueryParams();

	const ticket = queryParams.get('ticket');

	const impersonateUser = useCallback(async () => {
		if (!isLoaded || !ticket) {
			return;
		}

		try {
			/**
			 * When signing out, we want to stay on the same page
			 * to avoid unnecessary redirect to the login page.
			 * That's why we pass the current URL as the redirectUrl in the signOut function.
			 */
			if (isSignedIn) {
				await signOut({ redirectUrl: window.location.href });
			}

			shutdownIntercomChat();

			localStorage.removeItem(SESSION_ENV_KEY);

			dispatch({
				type: sessionNamespacer(actionTypes.session.REMOVE_SESSION),
				payload: { value: false },
			});

			// Create a new session using the impersonation ticket
			const { createdSessionId } = await signIn.create({
				strategy: 'ticket',
				ticket,
			});

			await setActive({
				session: createdSessionId,
				redirectUrl: '/?clerkAuthentication=true',
			});
		} catch (error) {
			handleException({ error });
		}
	}, [
		isLoaded,
		ticket,
		isSignedIn,
		signIn,
		dispatch,
		setActive,
		signOut,
		shutdownIntercomChat,
	]);

	useEffect(() => {
		impersonateUser();
	}, [impersonateUser]);

	return <BlockingLoader />;
};
